import Axios from "axios";
import { parseURLParams } from "../Utils/Utils";
import { ENDPOINT_EMPLOYEE_ME, ENDPOINT_EMPLOYEE_ME_PROCESS, ENDPOINT_EMPLOYEE_ME_SUBPROCESS, 
  ENDPOINT_EMPLOYEE_SUBPROCESS_DETAIL, ENDPOINT_EMPLOYEE_SUBPROCESS_FILE, ENDPOINT_NEW_TASK, 
  ENDPOINT_PROCESS_DETAIL, ENDPOINT_PROCESS_SUMMARY, ENDPOINT_SUBPROCESS_ALL, ENDPOINT_PROCESS_STATUSES, 
  ENDPOINT_PROCESS_TYPES, ENDPOINT_DEPARTMENTS, ENDPOINT_UPDATE_TASK, ENDPOINT_UPDATE_SUBTASK, 
  ENDPOINT_EMPLOYEES, ENDPOINT_PATCH_POSITION_SPECIFIC, ENDPOINT_PATCH_ALPS_CODE, ENDPOINT_PATCH_FRECUENCY,
  ENDPOINT_CENTERS } from "../Constants/Global";
import { REACT_APP_API_URL } from '../Config';

/**
 * A service for making HTTP requests to the server. It handles communication with the API using the available endpoints.
 * 
 * @class Services/AxiosService
 * @property {function} config - Generates the configuration object for Axios requests, including the authorization token.
 * @property {function} employee_subprocess - Fetches subprocess data for the logged-in employee.
 * @property {function} subprocesses - Fetches all subprocesses.
 * @property {function} employee_subprocess_detail - Fetches details of a specific subprocess for the logged-in employee.
 * @property {function} employee_process_detail - Fetches details of a specific process for the logged-in employee.
 * @property {function} processes - Fetches process data for the logged-in employee.
 * @property {function} employee_detail - Fetches details of the logged-in employee.
 * @property {function} post_new_task - Posts a new task to the server.
 * @property {function} post_file - Uploads a file for a specific subprocess.
 * @property {function} process_summaries - Fetches summaries of all processes.
 * @property {function} process_statuses - Fetches all process statuses.
 * @property {function} process_types - Fetches all process types.
 * @property {function} departments - Fetches all departments.
 * @property {function} centers - Fetches all centers.
 * @property {function} update_task - Updates a specific task.
 * @property {function} update_subtask - Updates a specific subtask.
 * @property {function} delete_task - Deletes a specific task.
 * @property {function} employees - Fetches all employees.
 */
export const AxiosService = {
  /**
   * Generates the configuration object for Axios requests, including the authorization token.
   * 
   * @param {string} token - The authorization token.
   * @returns {Object} The Axios request configuration.
   */
  config(token) {    
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      }
    };
  },

  /**
   * Fetches subprocess data for the logged-in employee.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the subprocess data.
   */
  employee_subprocess: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_EMPLOYEE_ME_SUBPROCESS, AxiosService.config(token));
  },

  /**
   * Fetches all subprocesses.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the subprocesses data.
   */
  subprocesses: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_SUBPROCESS_ALL, AxiosService.config(token));
  },

  /**
   * Fetches details of a specific subprocess for the logged-in employee.
   * 
   * @param {string} token - The authorization token.
   * @param {string} id - The ID of the subprocess.
   * @returns {Promise} A promise that resolves to the subprocess details.
   */
  employee_subprocess_detail: async (token, id) => {
    return await Axios.get(REACT_APP_API_URL + parseURLParams(ENDPOINT_EMPLOYEE_SUBPROCESS_DETAIL, id), AxiosService.config(token));
  },

  /**
   * Fetches details of a specific process for the logged-in employee.
   * 
   * @param {string} token - The authorization token.
   * @param {string} id - The ID of the process.
   * @returns {Promise} A promise that resolves to the process details.
   */
  employee_process_detail: async (token, id) => {
    return await Axios.get(REACT_APP_API_URL + parseURLParams(ENDPOINT_PROCESS_DETAIL, id), AxiosService.config(token));
  },

  /**
   * Fetches process data for the logged-in employee.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the process data.
   */
  processes: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_EMPLOYEE_ME_PROCESS, AxiosService.config(token));
  },

  /**
   * Fetches details of the logged-in employee.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the employee details.
   */
  employee_detail: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_EMPLOYEE_ME, AxiosService.config(token));
  },

  /**
   * Posts a new task to the server.
   * 
   * @param {string} token - The authorization token.
   * @param {Object} payload - The task data to post.
   * @returns {Promise} A promise that resolves to the server response.
   */
  post_new_task: async (token, payload) => {
    //console.log("payload: ", payload)
    return await Axios.post(REACT_APP_API_URL + ENDPOINT_NEW_TASK, payload, AxiosService.config(token));
  },


   /**
    */
    //$$$$$$$$$$$$$$$$$
   patch_frecuency: async (token, payload, id) => {
     console.log("payload: ", payload)
     return await Axios.patch(REACT_APP_API_URL + parseURLParams(ENDPOINT_PATCH_FRECUENCY, id),payload, AxiosService.config(token));
   },


  /**
   */
   //$$$$$$$$$$$$$$$$$
  patch_position_specific: async (token, payload, id) => {
    console.log("payload: ", payload)
    //return await Axios.patch(REACT_APP_API_URL + ENDPOINT_PATCH_POSITION_SPECIFIC, payload, AxiosService.config(token));
    return await Axios.patch(REACT_APP_API_URL + parseURLParams(ENDPOINT_PATCH_POSITION_SPECIFIC, id),payload, AxiosService.config(token));
  },


    /**
     */
     //$$$$$$$$$$$$$$$$$
    patch_alps_code: async (token, payload, id) => {
      console.log("payload: ", payload)
      return await Axios.patch(REACT_APP_API_URL + parseURLParams(ENDPOINT_PATCH_ALPS_CODE, id),payload, AxiosService.config(token));
    },



  /**
   * Uploads a file for a subprocess.
   *
   * @param {string} token - Auth token
   * @param {string} subprocessId - subprocess id
   * @param {FormData} formData - file payload
   */
  post_file: async (token, subprocessId, formData) => {
    return await Axios.post(
      REACT_APP_API_URL + `/subprocesses/${subprocessId}/files`,
      formData,
      AxiosService.config(token)
    );
  },

  /**
   * Fetches summaries of all processes.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the process summaries.
   */
  process_summaries: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_PROCESS_SUMMARY, AxiosService.config(token));
  },

  /**
   * Fetches all process statuses.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the process statuses.
   */
  process_statuses: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_PROCESS_STATUSES, AxiosService.config(token));
  },

  /**
   * Fetches all process types.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the process types.
   */
  process_types: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_PROCESS_TYPES, AxiosService.config(token));
  },

  /**
   * Fetches all departments.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the departments data.
   */
  departments: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_DEPARTMENTS, AxiosService.config(token));
  },


  centers: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_CENTERS, AxiosService.config(token));
  },

  /**
   * Updates a specific task.
   * 
   * @param {string} token - The authorization token.
   * @param {string} processId - The ID of the task to update.
   * @param {Object} payload - The updated task data.
   * @returns {Promise} A promise that resolves to the server response.
   */
  update_task: async (token, processId, payload) => {
    return await Axios.patch(REACT_APP_API_URL + parseURLParams(ENDPOINT_UPDATE_TASK, processId), payload, AxiosService.config(token));
  },

  /**
   * Updates a specific subtask.
   * 
   * @param {string} token - The authorization token.
   * @param {string} processId - The ID of the subtask to update.
   * @param {Object} payload - The updated subtask data.
   * @returns {Promise} A promise that resolves to the server response.
   */
  update_subtask: async (token, processId, payload) => {
    return await Axios.patch(REACT_APP_API_URL + parseURLParams(ENDPOINT_UPDATE_SUBTASK, processId), payload, AxiosService.config(token));
  },

  /**
   * Deletes a specific task.
   * 
   * @param {string} token - The authorization token.
   * @param {string} processId - The ID of the task to delete.
   * @returns {Promise} A promise that resolves to the server response.
   */
  delete_task: async (token, processId) => {
    return await Axios.delete(REACT_APP_API_URL + parseURLParams(ENDPOINT_UPDATE_TASK, processId), AxiosService.config(token));
  },

  /**
   * Fetches all employees.
   * 
   * @param {string} token - The authorization token.
   * @returns {Promise} A promise that resolves to the employees data.
   */
  employees: async (token) => {
    return await Axios.get(REACT_APP_API_URL + ENDPOINT_EMPLOYEES, AxiosService.config(token));
  },

  /**
   * Fetches all files for a specific subprocess.
   *
   * @param {string} token - The authorization token.
   * @param {string} subprocessId - The subprocess ID.
   * @returns {Promise} Files list.
   */
  subprocess_files: async (token, subprocessId) => {
    return await Axios.get(
      REACT_APP_API_URL + `/subprocesses/${subprocessId}/files`,
      AxiosService.config(token)
    );
  },

  /**
   * Downloads a specific file by its ID.
   *
   * @param {string} token - The authorization token.
   * @param {string} fileId - The ID of the file to download.
   * @returns {Promise} A promise that resolves to the file blob.
   */
    download_file: async (token, fileId) => {
      return await Axios.get(
        REACT_APP_API_URL + `/files/${fileId}/download`,
        AxiosService.config(token)
      );
    },

    /**
     * Updates permissions for a specific file.
     *
     * @param {string} token - The authorization token.
     * @param {string} fileId - The ID of the file.
     * @param {Array} permissions - Array of department names allowed to access the file.
     * @returns {Promise} A promise that resolves to the server response.
     */
    update_file_permissions: async (token, fileId, permissions) => {
      return await Axios.put(
        REACT_APP_API_URL + `/files/${fileId}/permissions`,
        { permissions },
        AxiosService.config(token)
      );
    },

};
